import { useCalculatorController } from "../../context/context";
import { useEffect, useState } from "react";
import { truncateToTwo } from "../../../../utils/truncateToTwo";

export const useMetricTable = () => {
  const { controller } = useCalculatorController();
  const {
    calcFormData: {
      installs,
      cost_per_install,
      average_subscription_price,
      platform_commission,
      cr_install_to_trial = 0,
      cr_trial_to_subscription = 0,
      cr_subscription_to_renewals,
      cr_install_to_subscription = 0,
      isTrial,
    },
  } = controller;
  const [tableData, setTableData] = useState({
    revenueRowData: Array(11).fill(100),
    roasRowData: Array(11).fill(100),
    arpuRowData: Array(11).fill(100),
    arppuRowData: Array(11).fill(100),
    ltvRowData: Array(11).fill(100),
    arpasRowData: Array(11).fill(100),
    acquisitionCost: 0,
  });

  const getRevenueRenewalFirst = () => {
    let result = average_subscription_price;

    if (isTrial) {
      result =
        result *
        (installs *
          (cr_install_to_trial / 100) *
          (cr_trial_to_subscription / 100));
    } else {
      result = result * (installs * (cr_install_to_subscription / 100));
    }

    if (platform_commission) {
      result -= result * (platform_commission / 100);
      return truncateToTwo(result);
    } else {
      return truncateToTwo(result);
    }
  };

  const getRevenueRenewalNext = (prevRevenue, cr_subscription_to_renewal) => {
    let temp = average_subscription_price;

    if (isTrial) {
      temp =
        temp *
        (installs *
          (cr_install_to_trial / 100) *
          (cr_trial_to_subscription / 100) *
          (cr_subscription_to_renewal / 100));
    } else {
      temp =
        temp *
        (installs *
          (cr_install_to_subscription / 100) *
          (cr_subscription_to_renewal / 100));
    }

    if (platform_commission) {
      temp -= temp * (platform_commission / 100);
    }

    return truncateToTwo(prevRevenue + temp);
  };

  const getARPU = (revenue) => {
    return truncateToTwo(Number(revenue) / installs);
  };

  const getARPPU = (revenue) => {
    const temp = isTrial
      ? installs *
        (cr_install_to_trial / 100) *
        (cr_trial_to_subscription / 100)
      : installs * (cr_install_to_subscription / 100);

    return truncateToTwo(Number(revenue) / temp);
  };

  const getARPAS = (revenue) => {
    const temp1 = cr_install_to_trial
      ? installs * (cr_install_to_trial / 100)
      : installs;

    const temp2 = isTrial
      ? installs *
        (cr_install_to_trial / 100) *
        (cr_trial_to_subscription / 100)
      : installs * (cr_install_to_subscription / 100);

    const tempResult = Number(temp1) + Number(temp2);

    return truncateToTwo(revenue / tempResult);
  };

  const getROAS = (revenue) => {
    const temp = cost_per_install ? cost_per_install * installs : installs;

    const res = ((revenue - temp) / temp) * 100;

    return truncateToTwo(res);
  };

  const getLTV = (arpu, revenues) => {
    const averageRevenues =
      revenues.reduce(
        (accumulator, currentValue) => accumulator + Number(currentValue),
        0
      ) / revenues.length;

    return truncateToTwo(arpu * averageRevenues);
  };

  const getRevenueRowData = () => {
    const revenueFirst = getRevenueRenewalFirst();
    const data = [...tableData.revenueRowData];
    data[0] = revenueFirst;

    if (cr_subscription_to_renewals?.length > 0) {
      for (let i = 0; i < cr_subscription_to_renewals.length; i++) {
        data[i + 1] = getRevenueRenewalNext(
          data[i],
          cr_subscription_to_renewals[i].cr_subscription_to_renewal
        );
      }
    }
    return data;
  };

  const getAcquisitionCost = () => {
    const temp1 = cost_per_install ? installs * cost_per_install : installs;
    const temp2 = isTrial
      ? installs *
        (cr_install_to_trial / 100) *
        (cr_trial_to_subscription / 100)
      : installs * (cr_install_to_subscription / 100);

    return truncateToTwo(temp1 / temp2);
  };

  const getRoasRowData = (revenues) => {
    return revenues?.map((elem) => getROAS(elem));
  };

  const getArpasRowData = (revenues) => {
    return revenues?.map((elem) => getARPAS(elem));
  };

  const getArpuRowData = (revenues) => {
    return revenues?.map((elem) => getARPU(elem));
  };

  const getArppuRowData = (revenues) => {
    return revenues?.map((elem) => getARPPU(elem));
  };

  const getLTVRowData = (arpus, revenues) => {
    return arpus?.map((elem) => getLTV(elem, revenues));
  };

  const calculateTableData = () => {
    const revenueRowData = getRevenueRowData();
    const roasRowData = getRoasRowData(revenueRowData);
    const arpuRowData = getArpuRowData(revenueRowData);
    const arppuRowData = getArppuRowData(revenueRowData);
    const ltvRowData = getLTVRowData(arpuRowData, revenueRowData);
    const arpasRowData = getArpasRowData(revenueRowData);
    const acquisitionCost = getAcquisitionCost();

    setTableData({
      revenueRowData,
      roasRowData,
      arpuRowData,
      arppuRowData,
      ltvRowData,
      acquisitionCost,
      arpasRowData,
    });
  };

  useEffect(() => {
    calculateTableData();
  }, [controller]);

  return {
    tableData,
  };
};
